import { useLatest } from 'ahooks';
import { useEffect, useRef } from 'react';
import {
    ITouchKitEvent,
    ITouchKitEventCallback,
    TouchKit,
    TouchKitEventType,
} from '@meitu/saas-editor-core/es/utils/event-kit';

const tk = new TouchKit();
const tkListenerList: {
    level: MoveListenerLevel;
    listenerRef: { current: ITouchKitEventCallback };
}[] = [];
tk.on(TouchKitEventType.Move, (e) => {
    const listenerList = tkListenerList.sort((a, b) => b.level - a.level).map((item) => item.listenerRef.current);
    listenerList.forEach((listener) => {
        listener(e);
    });
});

export enum MoveListenerLevel {
    /** 最优先的等级, 只可做取值操作, 不可有修改操作 */
    PREPARE_LEVEL = 1000,
    /** 默认等级 */
    DEFAULT_LEVEL = 0,
}

export const useMoveEventListener = (
    handler: (event: ITouchKitEvent) => void,
    /** level越大, 越先执行 */
    level = MoveListenerLevel.DEFAULT_LEVEL,
) => {
    const startMoveEvent = useRef<ITouchKitEvent | null>(null);
    const realMoveCallbackCount = useRef(0);
    const listener = (event: ITouchKitEvent) => {
        if (event.isStart) {
            startMoveEvent.current = event;
            return;
        }
        if (event.isEnd) {
            if (realMoveCallbackCount.current > 0) {
                handler(event);
            }
            // reset
            startMoveEvent.current = null;
            realMoveCallbackCount.current = 0;
            return;
        }
        /**
         * 在Mac触控板上，即使手指没有移动，也有可能触发mousemove事件
         * 这可能是由于触控板的精确度和敏感度造成的。当手指接触触控板时，即使微微晃动或触摸压力发生微小变化，也会被视为移动。
         * 所以需要过滤掉
         */
        if (startMoveEvent.current && startMoveEvent.current.x === event.x && startMoveEvent.current.y === event.y)
            return;

        if (realMoveCallbackCount.current === 0 && startMoveEvent.current) {
            handler(startMoveEvent.current);
        }

        handler(event);
        realMoveCallbackCount.current++;
    };
    const listenerRef = useLatest(listener);

    useEffect(() => {
        // 加入事件队列
        tkListenerList.push({
            level,
            listenerRef,
        });

        // 移除事件队列
        return () => {
            const index = tkListenerList.findIndex((item) => item.listenerRef === listenerRef);
            if (index >= 0) {
                tkListenerList.splice(index, 1);
            }
        };
    }, []);
};
